// src/lib/voiceFeedback/firstRunNotice.ts
//
// Per dev-sync note 6793d1bb — on the *first* time the leads-mode voice
// coach speaks for a given user, surface a coachmark explaining that
// spoken feedback is on and where to change its settings. The actual
// coachmark UI is rendered separately; this module owns the persistence +
// signaling so the speak path stays tiny.
//
// Flag is per-browser (localStorage). If the user clears storage or moves
// machines they will see it again — that's the right behavior; the
// affordance is cheap and the alternative (server-side per-user flag)
// adds a network round-trip to a path that runs frequently.

const LS_VOICE_FEEDBACK_INTRO_SEEN = "bb-voice-feedback-intro-seen";
export const VOICE_FEEDBACK_FIRST_RUN_EVENT = "bb-voice-feedback-first-run";

export function hasSeenVoiceFeedbackFirstRun(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(LS_VOICE_FEEDBACK_INTRO_SEEN) === "true";
  } catch {
    return false;
  }
}

export function markVoiceFeedbackFirstRunSeen(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(LS_VOICE_FEEDBACK_INTRO_SEEN, "true");
  } catch {
    // ignore — we will simply prompt again next session, no worse than baseline
  }
}

/**
 * Fire the first-run coachmark exactly once per browser. Safe to call on
 * every speak() invocation; subsequent calls no-op after the first.
 *
 * Returns true if this call actually triggered the notice (caller can
 * delay the speak a beat to let the user notice the popup before audio
 * starts), false if the notice has already been shown.
 */
export function notifyVoiceFeedbackFirstRun(): boolean {
  if (hasSeenVoiceFeedbackFirstRun()) return false;
  markVoiceFeedbackFirstRunSeen();
  try {
    if (typeof window === "undefined") return true;
    window.dispatchEvent(new CustomEvent(VOICE_FEEDBACK_FIRST_RUN_EVENT));
  } catch {
    // ignore — flag is already set so we won't loop
  }
  return true;
}
